import { useState } from "react"
import { DateTime } from "luxon"
import { useFormikContext } from "formik"
import { Box, Divider, Grid, MenuItem, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FormikDropdown, FormikSwitch, FormikTextField } from "../../../../../shared/components/forms/Formik"
import { sanitizeAndLimitInput } from "../../../../../shared/components/createOrder/components/form/helpers"
import { INVALID_INPUT_KEY } from "../../../../nonAdminUser/reports/settings/wallets/refill/components/WalletsRefillManual"
import { FormikImageUpload } from "../../../../../shared/components/Formik/FormikImageUpload"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { AvailabilityDetailsField } from "./AvailabilityDetailsField"
import { FormikProductGroupField } from "./FormikProductGroupField"
import { CreateUpdateProductFormValues } from "../productsTypes"
import { FormikAllergensField } from "./FormikAllergensField"
import { DeadlineField, dayOptions } from "./DeadlineField"
import { FormikCurrencyField } from "./FormikCurrencyField"
import { OfferDetails } from "./offerDetails/OfferDetails"
import { ItemLimitsField } from "./ItemLimitsField"
import { FormikPriceListField } from "./FormikPriceListsField"
import { StockField } from "./stockManagement/StockField"
import { InfoBox } from "../../shops/components/formElements/InfoBox"

const useStyles = makeStyles()(() => ({
    hidePriceSwitch: {
        "& .MuiFormControl-root": {
            width: "10rem",
        },
    },
    generalRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    onSaleInput: {
        width: "5rem",
        "& .MuiInputBase-root": {
            height: "2.5rem",
        },
        "& .MuiInputBase-input": {
            textAlign: "right",
        },
    },
}))

type CreateUpdateProductFormContentProps = Readonly<{
    isEditMode?: boolean
}>

export function CreateUpdateProductFormContent({ isEditMode }: CreateUpdateProductFormContentProps) {
    const { classes } = useStyles()
    const { values, setFieldValue, errors } = useFormikContext<CreateUpdateProductFormValues>()
    const defaultTime = DateTime.fromISO("12:00")
    const [checkedDeadline, setCheckedDeadline] = useState(!!values?.deadline)
    const [checkedItemLimits, setCheckedItemLimits] = useState(!!values?.itemLimits)
    const [checkedOffer, setCheckedOffer] = useState(!!values?.offerDetails)
    const [offerDialogState, setOfferDialogState] = useState<boolean>(false)
    const [cachedDeadline, setCachedDeadline] = useState(values?.deadline)
    const [cachedItemLimits, setCachedItemLimits] = useState(values?.itemLimits)
    const handleChangeDeadline = (checked: boolean) => {
        setCheckedDeadline(checked)
        if (checked) {
            const restored = cachedDeadline ?? {
                purchases: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
                cancellation: { daysBefore: dayOptions[0].value, timeOfDay: defaultTime },
            }
            setFieldValue("deadline", restored)
        } else {
            setCachedDeadline(values?.deadline)
            setFieldValue("deadline", null)
        }
    }
    const handleChangeItemLimits = (checked: boolean) => {
        setCheckedItemLimits(checked)
        if (checked) {
            const restored = cachedItemLimits ?? {
                maxDailyItemsPerPerson: "",
                minItemsPerOrder: "",
            }
            setFieldValue("itemLimits", restored)
        } else {
            setCachedItemLimits(values?.itemLimits)
            setFieldValue("itemLimits", null)
        }
    }

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    {/* <FormikPriceListsField name="priceLists" /> */}
                    <FormikPriceListField name="priceList" />

                    <FormikProductGroupField name="productGroup" />

                    <LocalizedStrict id="product-create-name-field-label" attrs={{ label: true }}>
                        <FormikTextField name="name" label="Product name" fullWidth />
                    </LocalizedStrict>

                    <Grid container item spacing={1} direction="row" alignItems="flex-start">
                        <Grid item sm={2.5}>
                            <LocalizedStrict id="product-create-currency-field-label" attrs={{ label: true }}>
                                <FormikDropdown name="currency" label="Currency" fullWidth>
                                    <MenuItem value="DKK">DKK</MenuItem>
                                </FormikDropdown>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item sm={3}>
                            <LocalizedStrict id="product-create-price-field-label" attrs={{ label: true }}>
                                <FormikCurrencyField name="price" label="Price" errorMessage={errors.price} />
                            </LocalizedStrict>
                        </Grid>
                        <Grid
                            item
                            sm
                            flexGrow={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            height="4rem"
                            className={classes.hidePriceSwitch}
                        >
                            <LocalizedStrict id="product-create-payment-price-switch">
                                <FormikSwitch name="paymentDetails.isPriceHidden">Hide price:</FormikSwitch>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </StandardMainPanel>
                <LocalizedStrict id="product-create-panel-description" attrs={{ title: true }}>
                    <StandardMainPanel title="Description">
                        <LocalizedStrict
                            id="product-create-product-description-marketing-tag-field"
                            attrs={{ label: true, helperText: true }}
                        >
                            <FormikTextField
                                name="productDescription.marketingTag"
                                label="Marketing tag"
                                helperText="Examples: 'Offer', 'CO2', 'Hot dish', 'Vegetarian'"
                                fullWidth
                            />
                        </LocalizedStrict>
                        <FormikAllergensField name="productDescription.allergens" />
                        <LocalizedStrict id="product-create-description-field-label" attrs={{ label: true }}>
                            <FormikTextField name="description" label="Description" rows={4} fullWidth multiline />
                        </LocalizedStrict>
                        <LocalizedStrict id="product-create-image-field-label" attrs={{ label: true }}>
                            <FormikImageUpload label="Image" name="productPhoto" typeOfEntity="product" />
                        </LocalizedStrict>
                    </StandardMainPanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-general-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="product-create-active-switch">
                            <FormikSwitch name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>

                        <Divider />
                        <Box className={classes.generalRow}>
                            <LocalizedStrict id="product-stock-on-sale-per-day-label">
                                <Typography variant="body1">On sale per day:</Typography>
                            </LocalizedStrict>
                            <FormikTextField
                                name="stock.itemsForSalePerDay"
                                type="number"
                                className={classes.onSaleInput}
                                size="small"
                                inputProps={{
                                    min: 0,
                                    inputMode: "numeric" as const,
                                    onInput: (e: React.FormEvent<HTMLInputElement>) => {
                                        const sanitized = sanitizeAndLimitInput(e.currentTarget.value, 6)
                                        if (e.currentTarget.value !== sanitized) {
                                            e.currentTarget.value = sanitized
                                        }
                                    },
                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (INVALID_INPUT_KEY.includes(e.key)) {
                                            e.preventDefault()
                                        }
                                    },
                                }}
                            />
                        </Box>
                        <LocalizedStrict id="product-stock-on-sale-per-day-info" attrs={{ description: true }}>
                            <InfoBox description="Using this you can limit the number of items on sale each day." />
                        </LocalizedStrict>
                        {isEditMode && (
                            <>
                                <Divider />
                                <StockField />
                            </>
                        )}
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-offer-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Offer"
                        variant="button"
                        checked={checkedOffer}
                        expanded={Boolean(values.offerDetails)}
                        onCreateButtonClick={() => setOfferDialogState(true)}
                    >
                        <OfferDetails
                            dialogState={offerDialogState}
                            setDialogState={setOfferDialogState}
                            setCheckedOffer={setCheckedOffer}
                        />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-availability-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Availability" variant="collapsible" expanded={false}>
                        <AvailabilityDetailsField />
                    </StandardSidePanel>
                </LocalizedStrict>
                <LocalizedStrict id="product-create-deadline-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Deadline"
                        variant="collapsibleWithToggle"
                        checked={checkedDeadline}
                        onCheckChange={handleChangeDeadline}
                        offContent="Deadline not activated"
                    >
                        <DeadlineField />
                    </StandardSidePanel>
                </LocalizedStrict>

                <LocalizedStrict id="product-create-order-limits-panel" attrs={{ title: true }}>
                    <StandardSidePanel
                        title="Order limits"
                        variant="collapsibleWithToggle"
                        checked={checkedItemLimits}
                        onCheckChange={handleChangeItemLimits}
                        offContent="Order limits not activated"
                    >
                        <ItemLimitsField />
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>
        </>
    )
}
